// Canonical error model surfacing (R20 / conventions §11). Every API failure is
// read as the canonical {code, message} body and shown as a human message keyed
// off `code`. This module is pure (no DOM/SDK dependency in the core map) so it is
// node-unit-testable.

export interface ApiError {
  code: string;
  message: string;
  status?: number;
  /** Present when password auth answered 401 {mfaId}: the pending MFA session id. */
  mfaId?: string;
  /** The error body's `details` array (e.g. schema_invalid findings), when present. */
  details?: unknown[];
}

// code -> human message. Each entry is distinct and non-empty. Unknown codes fall
// back to the server-supplied message (see errorMessage).
const MESSAGES: Record<string, string> = {
  name_taken:
    "Already published: the name is taken, or this exact document already exists as a version of this package.",
  dependency_conflict:
    "A dependency does not exist or would create a cycle. Fix the dependencies and try again.",
  not_canonicalizable:
    "The document could not be canonicalized. Check the YAML structure and try again.",
  version_not_monotonic:
    "The new version must be strictly higher than the current highest version.",
  forbidden: "You do not have permission to perform this action.",
  unauthorized: "Please sign in to continue.",
  version_yanked: "This version has been yanked and is no longer available.",
  rate_limited: "Too many requests — please wait a moment and retry shortly.",
  scope_not_found: "That scope does not exist.",
  package_not_found: "That package does not exist.",
  version_not_found: "That version does not exist.",
  tag_not_found: "That tag does not exist.",
  digest_mismatch:
    "The document digest does not match its content. The upload may be corrupted.",
  internal: "Something went wrong on the server. Please try again.",
  mfa_required: "A second authentication factor is required.",
  invalid_totp: "That code is not valid. Check your authenticator app and try again.",
  user_not_found: "No account matches that handle or email.",
  schema_invalid: "The document failed validation. Fix the reported findings and publish again.",
  preview_not_ready: "The preview is still being generated — try again shortly.",
  preview_not_found: "No preview is available for this version.",
};

/**
 * errorMessage maps a canonical error code to a human-readable message. Known
 * codes return a curated message; an unknown code falls back to the server's own
 * message, and finally to a generic message if neither is available.
 */
export function errorMessage(code: string, serverMessage?: string): string {
  const known = MESSAGES[code];
  if (known) return known;
  if (serverMessage && serverMessage.trim() !== "") return serverMessage;
  return "Something went wrong. Please try again.";
}

/**
 * parseError normalizes anything thrown by the PocketBase SDK or a raw fetch into
 * the canonical {code, message, status}. The SDK's ClientResponseError carries the
 * parsed JSON body on `.response`/`.data` and the HTTP status on `.status`.
 */
export function parseError(e: unknown): ApiError {
  if (e && typeof e === "object") {
    const any = e as Record<string, unknown>;
    const status = typeof any.status === "number" ? (any.status as number) : undefined;
    // SDK ClientResponseError: body is on `.response` (and legacy `.data`).
    const body = (any.response ?? any.data) as Record<string, unknown> | undefined;
    if (body && typeof body === "object") {
      const code = typeof body.code === "string" ? (body.code as string) : "";
      const message = typeof body.message === "string" ? (body.message as string) : "";
      // Password auth on an MFA-enrolled account answers 401 {mfaId} (the one
      // intentional exception to the {code,message} model).
      const mfaId = typeof body.mfaId === "string" ? (body.mfaId as string) : "";
      if (mfaId) {
        return { code: code || "mfa_required", message, status, mfaId };
      }
      if (code || message) {
        const details = Array.isArray(body.details) ? (body.details as unknown[]) : undefined;
        return { code: code || codeForStatus(status), message, status, ...(details ? { details } : {}) };
      }
    }
    // A thrown object that is already {code,message}.
    if (typeof any.code === "string" || typeof any.message === "string") {
      return {
        code: (any.code as string) || codeForStatus(status),
        message: (any.message as string) || "",
        status,
      };
    }
    if (status) return { code: codeForStatus(status), message: "", status };
  }
  return { code: "internal", message: e instanceof Error ? e.message : String(e ?? "") };
}

/** humanError is the convenience composition of parseError + errorMessage. */
export function humanError(e: unknown): string {
  const { code, message } = parseError(e);
  return errorMessage(code, message);
}

function codeForStatus(status?: number): string {
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "package_not_found";
    case 409:
      return "name_taken";
    case 410:
      return "version_yanked";
    case 422:
      return "not_canonicalizable";
    case 429:
      return "rate_limited";
    default:
      return "internal";
  }
}
